import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import theme from '../../../theme';
import {
    Button,
    Box,
    Menu,
    MenuItem,
    Typography,
    ListSubheader,
    Divider,
    Badge,
    Collapse,
    List,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { isOverdueEvent, isReminderEvent } from '../../../NotificationSystem';
import { usePermissions } from '../../../hooks/usePermissions';
import {
    canSeeCompanyFilter,
    canSeeDeferredCost,
    canSeeInternalOnlyFilter,
} from '../../../utils/roles';
import DeficiencyFilterMenu, {
    FilterSection,
    sectionHeaderSx,
    checkIconSx,
    countBadgeSx,
} from '../../global/Components/DeficiencyFilterMenu';

/**
 * Styled button component for consistent button styling
 */
const StyledButton = styled(Button)(() => ({
    textTransform: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 500,
}));

/**
 * Groups deficiencies by urgency in a single prioritized list.
 * Creates one list with urgency sorting: Overdue/action-needed → Due soon → Updates → Regular.
 *
 * @param {Array<Object>} deficiencies - Array of deficiency objects
 * @param {Function} getNotifications - Function to get notifications for a deficiency
 * @returns {Object} Object with 'All Deficiencies' key containing sorted array of deficiencies
 */
export const sortByUrgency = (deficiencies, getNotifications) => {
    const isRejectionEvent = (eventKey) =>
        ['INTERNAL_REJECTION', 'EXTERNAL_REJECTION', 'WORK_REJECTED'].includes(eventKey);

    const getDueDateTime = (deficiency) => {
        if (!deficiency.dueDate) return Number.POSITIVE_INFINITY;
        const dueDate = new Date(deficiency.dueDate).getTime();
        return Number.isNaN(dueDate) ? Number.POSITIVE_INFINITY : dueDate;
    };

    const getUpdatedTime = (deficiency) => {
        const updatedDate = new Date(deficiency.lastUpdated || deficiency.createdAt || 0).getTime();
        return Number.isNaN(updatedDate) ? 0 : updatedDate;
    };

    // Helper function to get urgency level for sorting.
    const getUrgencyLevel = (deficiency) => {
        const notifications = getNotifications ? getNotifications(deficiency.deficiencyId) : [];
        const dueDateState = deficiency?.dueDateState;

        const hasEscalation = notifications.some((n) => isOverdueEvent(n.eventKey));
        const hasRejection = notifications.some((n) => isRejectionEvent(n.eventKey));
        const hasReminder = notifications.some((n) => isReminderEvent(n.eventKey));
        const hasNotifications = notifications.length > 0;

        if (dueDateState === 'OVERDUE' || hasEscalation || hasRejection) return 1;
        if (dueDateState === 'DUE_SOON' || hasReminder) return 2;
        if (hasNotifications) return 3;
        return 4;
    };

    // Sort by urgency first, then earliest due date, then most recently updated.
    const sortedDeficiencies = [...deficiencies].sort((a, b) => {
        const aUrgency = getUrgencyLevel(a);
        const bUrgency = getUrgencyLevel(b);

        if (aUrgency !== bUrgency) {
            return aUrgency - bUrgency;
        }

        const dueDateDifference = getDueDateTime(a) - getDueDateTime(b);
        if (dueDateDifference !== 0) {
            return dueDateDifference;
        }

        return getUpdatedTime(b) - getUpdatedTime(a);
    });

    return {
        'All Deficiencies': sortedDeficiencies,
    };
};

/**
 * Sorts deficiencies by due date ascending, nulls last
 *
 * @param {Array<Object>} deficiencies - Array of deficiency objects
 * @returns {Object} Object with 'All Deficiencies' key containing sorted array
 */
export const sortByDueDate = (deficiencies) => {
    const sorted = [...deficiencies].sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
    });
    return { 'All Deficiencies': sorted };
};

/**
 * Sorts deficiencies by creation date, newest first
 *
 * @param {Array<Object>} deficiencies - Array of deficiency objects
 * @returns {Object} Object with 'All Deficiencies' key containing sorted array
 */
export const sortByCreationDate = (deficiencies) => {
    const sorted = [...deficiencies].sort((a, b) => {
        const aDate = new Date(a.dateCreated || a.createdAt || 0);
        const bDate = new Date(b.dateCreated || b.createdAt || 0);
        return bDate - aDate;
    });
    return { 'All Deficiencies': sorted };
};

/**
 * Sorts deficiencies by deferred cost descending, nulls/0 last
 *
 * @param {Array<Object>} deficiencies - Array of deficiency objects
 * @returns {Object} Object with 'All Deficiencies' key containing sorted array
 */
export const sortByDeferredCost = (deficiencies) => {
    const sorted = [...deficiencies].sort((a, b) => {
        const aCost = a.deferredCost || 0;
        const bCost = b.deferredCost || 0;
        if (aCost === 0 && bCost === 0) return 0;
        if (aCost === 0) return 1;
        if (bCost === 0) return -1;
        return bCost - aCost;
    });
    return { 'All Deficiencies': sorted };
};

/**
 * Buttons component for deficiency sorting, filtering, and batch operations
 * Provides a combined Sort/Filter menu and batch mode toggle
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onSortChange - Callback for when sort type changes
 * @param {string} props.currentSort - Current sort type ('urgency', 'dueDate', 'dateCreated', 'deferredCost')
 * @param {boolean} props.isBatchMode - Whether batch mode is currently active
 * @param {Function} props.onBatchModeChange - Callback for toggling batch mode
 * @param {Array<Object>} props.deficiencies - Unfiltered deficiencies for deriving filter options
 * @param {Array<string>} props.statusFilters - Currently active status filters (multi-select)
 * @param {Array<Object>} props.companyFilters - Currently active company filters (multi-select)
 * @param {Array<Object>} props.locationFilters - Currently active location filters (multi-select)
 * @param {Function} props.onToggleStatus - Callback to toggle a status filter
 * @param {Function} props.onToggleCompany - Callback to toggle a company filter
 * @param {Function} props.onToggleLocation - Callback to toggle a location filter
 * @param {boolean} props.internalOnlyFilter - Whether internal-only filter is active
 * @param {Function} props.onToggleInternalOnly - Callback to toggle internal-only filter
 * @param {boolean} props.showCompleted - Whether to show completed deficiencies
 * @param {Function} props.onToggleShowCompleted - Callback to toggle show completed filter
 * @param {boolean} props.showRejected - Whether to show rejected deficiencies
 * @param {Function} props.onToggleShowRejected - Callback to toggle show rejected filter
 * @returns {React.ReactElement} Button controls
 */
const Buttons = ({
    onSortChange,
    currentSort,
    isBatchMode,
    onBatchModeChange,
    deficiencies = [],
    statusFilters = [],
    companyFilters = [],
    locationFilters = [],
    onToggleStatus,
    onToggleCompany,
    onToggleLocation,
    internalOnlyFilter = false,
    onToggleInternalOnly,
    showCompleted = false,
    onToggleShowCompleted,
    showRejected = false,
    onToggleShowRejected,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [expandedFloorplans, setExpandedFloorplans] = useState({});
    const [locationExpanded, setLocationExpanded] = useState(false);

    const { projectId } = useParams();
    const { getProjectRole } = usePermissions();
    const role = getProjectRole(projectId);
    const isManager = ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_ADMIN'].includes(role);
    const showCompanyFilter = canSeeCompanyFilter(role);
    const showInternalOnlyFilter = canSeeInternalOnlyFilter(role);
    const showDeferredCost = canSeeDeferredCost(role);

    const activeFilterCount =
        statusFilters.length +
        (showCompanyFilter ? companyFilters.length : 0) +
        locationFilters.length +
        (showInternalOnlyFilter && internalOnlyFilter ? 1 : 0) +
        (showCompleted ? 1 : 0) +
        (showRejected ? 1 : 0);

    // Group rooms by floorplan for hierarchical location filter
    const roomsByFloorplan = useMemo(() => {
        const fpMap = {};
        deficiencies.forEach((d) => {
            const fp = d.floorplanName || 'Unknown Floor Plan';
            const room = d.roiName || 'Unassigned Room';
            if (!fpMap[fp])
                fpMap[fp] = { rooms: new Set(), groupName: d.floorplanGroupName || null };
            fpMap[fp].rooms.add(room);
        });
        const sorted = {};
        Object.keys(fpMap)
            .sort()
            .forEach((fp) => {
                sorted[fp] = { rooms: [...fpMap[fp].rooms].sort(), groupName: fpMap[fp].groupName };
            });
        return sorted;
    }, [deficiencies]);

    // Group floorplans by their group name for display
    const floorplansByGroup = useMemo(() => {
        const hasAnyGroupName = Object.values(roomsByFloorplan).some((v) => v.groupName);
        if (!hasAnyGroupName) return null;

        const groups = {};
        Object.entries(roomsByFloorplan).forEach(([fp, { groupName }]) => {
            const key = groupName || 'Ungrouped';
            if (!groups[key]) groups[key] = [];
            groups[key].push(fp);
        });

        // Sort: named groups first alphabetically, Ungrouped last
        return Object.entries(groups).sort(([a], [b]) => {
            if (a === 'Ungrouped') return 1;
            if (b === 'Ungrouped') return -1;
            return a.localeCompare(b);
        });
    }, [roomsByFloorplan]);

    // Helper: is a specific room selected?
    const isRoomSelected = (floorplan, room) =>
        locationFilters.some((l) => l.floorplanName === floorplan && l.roomName === room);

    // Helper: is an entire floorplan selected?
    const isFloorplanSelected = (floorplan) =>
        locationFilters.some((l) => l.floorplanName === floorplan && l.roomName === null);

    // Helper: count selected items for a floorplan (rooms + floorplan-level)
    const selectedCountForFloorplan = (floorplan) =>
        locationFilters.filter((l) => l.floorplanName === floorplan).length;

    // Renders a single floorplan row with expandable rooms
    const renderFloorplanItem = (floorplan, plLeft, roomPlLeft) => {
        const { rooms } = roomsByFloorplan[floorplan];
        const fpSelectedCount = selectedCountForFloorplan(floorplan);
        const fpSelected = isFloorplanSelected(floorplan);
        return (
            <React.Fragment key={floorplan}>
                <ListItemButton
                    sx={{
                        pl: plLeft,
                        py: 0.5,
                        backgroundColor: fpSelected
                            ? `${theme.palette.primary.main}08`
                            : 'transparent',
                        '&:hover': {
                            backgroundColor: theme.palette.grey[50],
                        },
                    }}
                >
                    <ListItemText
                        primary={floorplan}
                        onClick={() =>
                            onToggleLocation({
                                floorplanName: floorplan,
                                roomName: null,
                            })
                        }
                        slotProps={{
                            primary: {
                                sx: {
                                    fontSize: '0.78rem',
                                    fontWeight: fpSelectedCount > 0 ? 600 : 500,
                                    color:
                                        fpSelectedCount > 0
                                            ? theme.palette.primary.main
                                            : theme.palette.grey[700],
                                },
                            },
                        }}
                    />
                    {fpSelected && <CheckIcon sx={checkIconSx} />}
                    {!fpSelected && fpSelectedCount > 0 && (
                        <Typography variant="caption" sx={countBadgeSx}>
                            {fpSelectedCount}
                        </Typography>
                    )}
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFloorplan(floorplan);
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            ml: 0.5,
                        }}
                    >
                        {expandedFloorplans[floorplan] ? (
                            <ExpandLess
                                sx={{
                                    fontSize: 16,
                                    color: theme.palette.grey[400],
                                }}
                            />
                        ) : (
                            <ExpandMore
                                sx={{
                                    fontSize: 16,
                                    color: theme.palette.grey[400],
                                }}
                            />
                        )}
                    </Box>
                </ListItemButton>
                <Collapse in={expandedFloorplans[floorplan]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {rooms.map((room) => {
                            const selected = isRoomSelected(floorplan, room) || fpSelected;
                            return (
                                <ListItemButton
                                    key={`${floorplan}-${room}`}
                                    onClick={() =>
                                        onToggleLocation({
                                            floorplanName: floorplan,
                                            roomName: room,
                                        })
                                    }
                                    sx={{
                                        pl: roomPlLeft,
                                        py: 0.25,
                                        backgroundColor: selected
                                            ? `${theme.palette.primary.main}08`
                                            : 'transparent',
                                        '&:hover': {
                                            backgroundColor: theme.palette.grey[50],
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={room}
                                        slotProps={{
                                            primary: {
                                                sx: {
                                                    fontSize: '0.78rem',
                                                    fontWeight: selected ? 500 : 400,
                                                },
                                            },
                                        }}
                                    />
                                    {selected && <CheckIcon sx={checkIconSx} />}
                                </ListItemButton>
                            );
                        })}
                    </List>
                </Collapse>
            </React.Fragment>
        );
    };

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
        setLocationExpanded(false);
        setExpandedFloorplans({});
    };

    const handleSortChange = (option) => {
        onSortChange(option);
        handleFilterClose();
    };

    const handleToggleFloorplan = (floorplan) => {
        setExpandedFloorplans((prev) => ({
            ...prev,
            [floorplan]: !prev[floorplan],
        }));
    };

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isManager && (
                <StyledButton
                    variant={isBatchMode ? 'outlined' : 'contained'}
                    onClick={() => onBatchModeChange(!isBatchMode)}
                    sx={
                        isBatchMode
                            ? {
                                  borderColor: theme.palette.error.main,
                                  color: theme.palette.error.main,
                              }
                            : {
                                  backgroundColor: theme.palette.primary.main,
                                  color: 'white',
                              }
                    }
                >
                    {isBatchMode ? 'Cancel Selection' : 'Batch Action'}
                </StyledButton>
            )}

            <Badge
                badgeContent={activeFilterCount}
                color="primary"
                invisible={activeFilterCount === 0}
                sx={{
                    '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        height: 18,
                        minWidth: 18,
                    },
                }}
            >
                <StyledButton
                    variant="outlined"
                    onClick={handleFilterClick}
                    sx={{
                        borderColor:
                            activeFilterCount > 0
                                ? theme.palette.primary.main
                                : theme.palette.grey[200],
                        color:
                            activeFilterCount > 0
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                    }}
                >
                    <FilterListIcon sx={{ fontSize: 20 }} />
                </StyledButton>
            </Badge>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleFilterClose}
                slotProps={{
                    paper: {
                        sx: {
                            width: 260,
                            maxHeight: 480,
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                    },
                }}
            >
                {/* Sort By */}
                <ListSubheader sx={sectionHeaderSx}>Sort By</ListSubheader>
                {[
                    { key: 'urgency', label: 'Urgency (Overdue First)' },
                    { key: 'dueDate', label: 'Due Date' },
                    { key: 'dateCreated', label: 'Creation Date' },
                    ...(showDeferredCost ? [{ key: 'deferredCost', label: 'Deferred Cost' }] : []),
                ].map(({ key, label }) => (
                    <MenuItem
                        key={key}
                        selected={currentSort === key}
                        onClick={() => handleSortChange(key)}
                        dense
                        sx={{ px: 2, py: 0.75, fontSize: '0.875rem' }}
                    >
                        {label}
                    </MenuItem>
                ))}

                <Divider sx={{ my: 0.75 }} />

                <DeficiencyFilterMenu
                    deficiencies={deficiencies}
                    sections={{
                        status: true,
                        company: showCompanyFilter,
                        showCompleted: true,
                        showRejected: true,
                        internalOnly: showInternalOnlyFilter,
                    }}
                    filters={{
                        statusFilters,
                        companyFilters,
                        showCompleted,
                        showRejected,
                        internalOnly: internalOnlyFilter,
                    }}
                    onToggle={{
                        status: onToggleStatus,
                        company: onToggleCompany,
                        showCompleted: onToggleShowCompleted,
                        showRejected: onToggleShowRejected,
                        internalOnly: onToggleInternalOnly,
                    }}
                    showDivider={true}
                    role={role}
                >
                    {/* Location - floorplan → room hierarchy, unique to deficiency page */}
                    <FilterSection
                        label="Location"
                        expanded={locationExpanded}
                        onToggle={() => setLocationExpanded((prev) => !prev)}
                        selectedCount={locationFilters.length}
                    >
                        {floorplansByGroup
                            ? floorplansByGroup.map(([groupLabel, floorplans]) => (
                                  <React.Fragment key={groupLabel}>
                                      <ListSubheader
                                          sx={{
                                              pl: 3.5,
                                              py: 0.25,
                                              lineHeight: '28px',
                                              fontSize: '0.7rem',
                                              fontWeight: 700,
                                              textTransform: 'uppercase',
                                              letterSpacing: '0.05em',
                                              color: theme.palette.grey[500],
                                              backgroundColor: theme.palette.grey[50],
                                          }}
                                      >
                                          {groupLabel}
                                      </ListSubheader>
                                      {floorplans.map((floorplan) =>
                                          renderFloorplanItem(floorplan, 4.5, 6.5)
                                      )}
                                  </React.Fragment>
                              ))
                            : Object.keys(roomsByFloorplan).map((floorplan) =>
                                  renderFloorplanItem(floorplan, 3.5, 5.5)
                              )}
                    </FilterSection>
                </DeficiencyFilterMenu>
            </Menu>
        </Box>
    );
};

export default Buttons;
